/// <reference types='gapi.client.tasks' />
/// <reference types='gapi.auth2' />
import { Injectable, NgZone } from '@angular/core';
import { bindCallback, defer, from, Observable } from 'rxjs';
import { mergeMap, repeat } from 'rxjs/operators';

import GoogleAuth = gapi.auth2.GoogleAuth;

import TaskLists = gapi.client.tasks.TaskLists;
import TaskList = gapi.client.tasks.TaskList;
import Tasks = gapi.client.tasks.Tasks;
import Task = gapi.client.tasks.Task;

import { TasklistsDeleteParams, TasklistsGetParams, TasklistsInsertParams, TasklistsListParams, TasklistsPatchParams, TasklistsUpdateParams } from './tasklists-resource-method-params';
import { TasksClearParams, TasksDeleteParams, TasksGetParams, TasksInsertParams, TasksListParams, TasksMoveParams, TasksPatchParams, TasksUpdateParams } from './tasks-resource-method-params';
import { ScriptLoaderService } from './script-loader.service';

const DEBUG = true;

/** Constants used in the gapi init params */
const GOOGLE_PROJECT_ID = '338719905965-s2n31ac6jk0a17qf1846lrbh6804rc28.apps.googleusercontent.com';
const API_KEY = 'AIzaSyD0RgAPLq8Z7j-tgBkBNbsAM2pxUiTA_ZE';
const DISCOVERY_DOCS: string[] = ['https://www.googleapis.com/discovery/v1/apis/tasks/v1/rest'];
const SCOPE: string = ['https://www.googleapis.com/auth/tasks', 'https://www.googleapis.com/auth/tasks.readonly'].join(' ');

@Injectable({
  providedIn: 'root',
})
export class TasksApiService {

  onLoaded$: Observable<void> = defer(() => from(gapi.client.load('tasks', 'v1')).pipe(repeat(1)));
  googleAuth: GoogleAuth|null = null;

  constructor(private scriptLoader: ScriptLoaderService, private ngZone: NgZone) {
    if (DEBUG) {console.log('new TasksApiService()');}
    scriptLoader.onLoaded$.subscribe(() => {
      bindCallback<string,void>(gapi.load)('client:auth2').subscribe(() => {
        this.onLoaded$.subscribe();
      });
    });
  }

  listTaskLists$(req?: TasklistsListParams): Observable<TaskLists> {
    return this.onLoaded$.pipe(mergeMap(() => {
      return from(gapi.client.tasks.tasklists.list(req).then((response) => response.result));
    }));
  }

  async getTaskList(req: TasklistsGetParams): Promise<TaskList> {
    return gapi.client.tasks.tasklists.get(req).then((response) => response.result);
  }

  async insertTaskList(req: TasklistsInsertParams): Promise<TaskList> {
    return gapi.client.tasks.tasklists.insert(req).then((response) => response.result);
  }

  async updateTaskList(req: TasklistsUpdateParams): Promise<TaskList> {
    return gapi.client.tasks.tasklists.update(req).then((response) => response.result);
  }

  async patchTaskList(req: TasklistsPatchParams): Promise<TaskList> {
    return gapi.client.tasks.tasklists.patch(req).then((response) => response.result);
  }

  async deleteTaskList(req: TasklistsDeleteParams): Promise<void> {
    return gapi.client.tasks.tasklists.delete(req).then((response) => response.result);
  }

  listTasks$(req: TasksListParams): Observable<Tasks> {
    return from(gapi.client.tasks.tasks.list(req).then(response => response.result));
  }

  async getTask(req: TasksGetParams): Promise<Task> {
    return gapi.client.tasks.tasks.get(req).then((response) => response.result);
  }

  async insertTask(req: TasksInsertParams): Promise<Task> {
    return gapi.client.tasks.tasks.insert(req).then((response) => response.result);
  }

  async updateTask(req: TasksUpdateParams): Promise<Task> {
    return gapi.client.tasks.tasks.update(req).then((response) => response.result);
  }

  async patchTask(req: TasksPatchParams): Promise<Task> {
    return gapi.client.tasks.tasks.patch(req).then((response) => response.result);
  }

  async deleteTask(req: TasksDeleteParams): Promise<void> {
    return gapi.client.tasks.tasks.delete(req).then((response) => response.result);
  }

  async clearTasks(req: TasksClearParams): Promise<void> {
    if (DEBUG) {console.log('Making clear() request:', req);}
    return gapi.client.tasks.tasks.clear(req).then(response => {
      if (DEBUG) {console.log('Received response to clear() request:', response);}
      return response.result;
    });
  }

  async moveTask(req: TasksMoveParams): Promise<Task> {
    return gapi.client.tasks.tasks.move(req).then(response => response.result);
  }
}

export  const gapiTasksClientConfig = {
  apiKey: API_KEY,
  discoveryDocs: DISCOVERY_DOCS,
  clientId: GOOGLE_PROJECT_ID,
  scope: SCOPE,
  // hosted_domain: ''
};
